import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ExternalLink, AlertTriangle, Zap, MessageSquare, Image, Video, Music, Hash, Clock, Tag, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { updateNode, getFunnels, getFunnelDetails } from '@/services/api';
import type { FlowNode } from '@/types/flow';
import type { FunnelResponse, NodeResponse } from '@/types/funnel';

interface GoToConfigProps {
    node: FlowNode;
    onClose: () => void;
    currentFunnelPageIds?: string[];
}

type EntryMode = 'trigger' | 'specific';

// Mapa de tipos de node pra ícone + nome legível
const NODE_TYPE_META: Record<number, { label: string; icon: any; color: string }> = {
    1:  { label: 'Trigger',          icon: Zap,           color: 'text-green-500' },
    2:  { label: 'Mensagem',         icon: MessageSquare, color: 'text-blue-500' },
    3:  { label: 'Delay',            icon: Clock,         color: 'text-amber-500' },
    4:  { label: 'Foto + botões',    icon: Image,         color: 'text-pink-500' },
    5:  { label: 'Tag',              icon: Tag,           color: 'text-violet-500' },
    6:  { label: 'Vídeo + botões',   icon: Video,         color: 'text-rose-500' },
    7:  { label: 'Vídeo',            icon: Video,         color: 'text-rose-500' },
    8:  { label: 'Template Mensagem', icon: MessageCircle, color: 'text-blue-600' },
    9:  { label: 'Foto',             icon: Image,         color: 'text-pink-500' },
    10: { label: 'Áudio',            icon: Music,         color: 'text-purple-500' },
    11: { label: 'Go To',            icon: ExternalLink,  color: 'text-violet-600' },
    12: { label: 'Resposta pública', icon: MessageSquare, color: 'text-emerald-500' },
};

const nodeTypeLabel = (typeId: number) => NODE_TYPE_META[typeId]?.label ?? `Tipo ${typeId}`;
// Mantido pra uso futuro — Hash fica importado por isso.
const _nodeTypeIcon = (typeId: number) => NODE_TYPE_META[typeId] || { icon: Hash, color: 'text-gray-400' };
void _nodeTypeIcon;

export default function GoToConfig({ node, onClose, currentFunnelPageIds = [] }: GoToConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [funnelId, setFunnelId] = useState<number | null>(node.data.config.goto_funnel_id || null);
    const [targetNodeId, setTargetNodeId] = useState<number | null>(node.data.config.goto_trigger_node_id || null);
    const [allFunnels, setAllFunnels] = useState<FunnelResponse[]>([]);
    const [allNodes, setAllNodes] = useState<NodeResponse[]>([]);
    // Modo de entrada: pelo trigger (início do flow) ou em um node específico.
    // Sempre começa em 'trigger' — ajustado após carregar os nodes (ver useEffect abaixo).
    const initialMode: EntryMode = 'trigger';
    const [entryMode, setEntryMode] = useState<EntryMode>(initialMode);

    // Lista TODOS os flows (ativos e inativos): o destino de um Go To não
    // precisa estar ativo — flow inativo funciona como sub-flow (só entra
    // por Go To, nunca pelos gatilhos dele). Ativos primeiro.
    useEffect(() => {
        getFunnels().then(setAllFunnels).catch(() => {});
    }, []);

    const funnels = [...allFunnels].sort(
        (a, b) => Number(b.is_active) - Number(a.is_active) || a.nome.localeCompare(b.nome, 'pt-BR')
    );

    const selectedFunnelPages = funnelId ? (allFunnels.find(f => f.id === funnelId)?.pages ?? []).map(p => p.page_id) : [];
    const selectedFunnelIncompatible = funnelId && currentFunnelPageIds.length > 0
        && !currentFunnelPageIds.every(pid => selectedFunnelPages.includes(pid));

    useEffect(() => {
        if (!funnelId) {
            setAllNodes([]);
            return;
        }
        getFunnelDetails(funnelId)
            .then(detail => {
                setAllNodes(detail.nodes || []);
                // Após carregar nodes, ajusta o entryMode olhando o tipo
                if (targetNodeId) {
                    const t = (detail.nodes || []).find(n => n.id === targetNodeId);
                    if (t && t.node_type_id !== 1) {
                        setEntryMode('specific');
                    } else {
                        setEntryMode('trigger');
                    }
                }
            })
            .catch(() => setAllNodes([]));
    }, [funnelId]);

    // Listas derivadas
    const triggerNodes = useMemo(
        () => allNodes.filter(n => n.is_trigger || n.node_type_id === 1),
        [allNodes],
    );
    const nonTriggerNodes = useMemo(
        () => allNodes.filter(n => !n.is_trigger && n.node_type_id !== 1),
        [allNodes],
    );

    const updateMutation = useMutation({
        mutationFn: (data: any) => updateNode(node.data.id, data),
        onSuccess: () => {
            toast({ title: 'Salvo!', description: 'Configuração do Go To atualizada.' });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const handleSave = () => {
        if (!funnelId || !targetNodeId) {
            toast({
                variant: 'destructive',
                title: 'Selecione um flow',
                description: 'Escolha o flow e o node onde entrar.',
            });
            return;
        }

        updateMutation.mutate({
            nome: nodeName,
            goto_funnel_id: funnelId,
            goto_trigger_node_id: targetNodeId,
        });
    };

    const isSaving = updateMutation.isPending;
    const selectedFunnel = funnels.find(f => f.id === funnelId);
    const selectedNode = allNodes.find(n => n.id === targetNodeId);

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    <div>
                        <Label htmlFor="node-name">Nome do Node (opcional)</Label>
                        <Input
                            id="node-name"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder="Go To"
                            className="mt-1.5"
                            disabled={isSaving}
                        />
                    </div>

                    <div className="space-y-3 p-4 bg-violet-50 rounded-lg border border-violet-200">
                        <div className="flex items-center gap-2 mb-2">
                            <ExternalLink className="w-4 h-4 text-violet-600" />
                            <p className="text-sm font-medium text-violet-900">Redirecionar para Flow</p>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs text-gray-600">Funil *</Label>
                            <select
                                value={funnelId ?? ''}
                                onChange={e => {
                                    const id = e.target.value ? Number(e.target.value) : null;
                                    setFunnelId(id);
                                    setTargetNodeId(null);
                                    setEntryMode('trigger');
                                }}
                                disabled={isSaving}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                            >
                                <option value="">Selecione um flow...</option>
                                {funnels.map(f => (
                                    <option key={f.id} value={f.id}>{f.nome}{f.is_active ? '' : ' · inativo'}</option>
                                ))}
                            </select>
                        </div>

                        {funnelId && (
                            <>
                                {/* Onde entrar no flow */}
                                <div className="space-y-2">
                                    <Label className="text-xs text-gray-600">Onde entrar *</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            disabled={isSaving}
                                            onClick={() => { setEntryMode('trigger'); setTargetNodeId(null); }}
                                            className={`p-2.5 rounded-lg border-2 transition-colors text-left ${entryMode === 'trigger'
                                                ? 'border-violet-500 bg-violet-100'
                                                : 'border-gray-200 bg-white hover:border-gray-300'}`}
                                        >
                                            <Zap className={`w-4 h-4 mb-1 ${entryMode === 'trigger' ? 'text-violet-600' : 'text-gray-400'}`} />
                                            <p className="text-xs font-semibold">Pelo trigger</p>
                                            <p className="text-[10px] text-gray-500">Início do flow (entrada normal)</p>
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isSaving}
                                            onClick={() => { setEntryMode('specific'); setTargetNodeId(null); }}
                                            className={`p-2.5 rounded-lg border-2 transition-colors text-left ${entryMode === 'specific'
                                                ? 'border-blue-500 bg-blue-100'
                                                : 'border-gray-200 bg-white hover:border-gray-300'}`}
                                        >
                                            <Hash className={`w-4 h-4 mb-1 ${entryMode === 'specific' ? 'text-blue-600' : 'text-gray-400'}`} />
                                            <p className="text-xs font-semibold">Node específico</p>
                                            <p className="text-[10px] text-gray-500">Pula direto pra um node do meio</p>
                                        </button>
                                    </div>
                                </div>

                                {/* Seletor do node alvo (filtra por modo) */}
                                {entryMode === 'trigger' && (
                                    <div className="space-y-2">
                                        <Label className="text-xs text-gray-600">Trigger de entrada *</Label>
                                        <select
                                            value={targetNodeId ?? ''}
                                            onChange={e => setTargetNodeId(e.target.value ? Number(e.target.value) : null)}
                                            disabled={isSaving || triggerNodes.length === 0}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                                        >
                                            <option value="">Selecione o trigger...</option>
                                            {triggerNodes.map(n => (
                                                <option key={n.id} value={n.id}>
                                                    {n.nome || `Trigger #${n.id}`}
                                                    {n.triggers?.[0]?.trigger_value ? ` — "${n.triggers[0].trigger_value}"` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {triggerNodes.length === 0 && (
                                            <p className="text-xs text-amber-600">Este funil não tem nodes de trigger.</p>
                                        )}
                                    </div>
                                )}

                                {entryMode === 'specific' && (
                                    <div className="space-y-2">
                                        <Label className="text-xs text-gray-600">Node alvo *</Label>
                                        <select
                                            value={targetNodeId ?? ''}
                                            onChange={e => setTargetNodeId(e.target.value ? Number(e.target.value) : null)}
                                            disabled={isSaving || nonTriggerNodes.length === 0}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                                        >
                                            <option value="">Selecione um node...</option>
                                            {nonTriggerNodes.map(n => (
                                                <option key={n.id} value={n.id}>
                                                    [{nodeTypeLabel(n.node_type_id)}] {n.nome || `Node #${n.id}`}
                                                </option>
                                            ))}
                                        </select>
                                        {nonTriggerNodes.length === 0 && (
                                            <p className="text-xs text-amber-600">Este funil ainda não tem outros nodes além do trigger.</p>
                                        )}
                                        <p className="text-[11px] text-gray-500 leading-relaxed">
                                            O lead vai pular direto pra esse node — útil pra entrar no meio do flow
                                            (ex.: após uma decisão, ir direto pra um node de venda sem reiniciar).
                                        </p>
                                    </div>
                                )}
                            </>
                        )}

                        {selectedFunnelIncompatible && (
                            <div className="flex gap-2 p-3 bg-amber-50 rounded border border-amber-300 mt-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800">
                                    O flow selecionado não contém todas as páginas deste flow. O lead só será redirecionado se estiver em uma página compatível.
                                </p>
                            </div>
                        )}

                        {selectedFunnel && targetNodeId && selectedNode && (
                            <div className="p-3 bg-white rounded border border-violet-200 mt-2">
                                <p className="text-xs text-violet-700">
                                    O lead será redirecionado para o flow <strong>{selectedFunnel.nome}</strong>
                                    {selectedNode.node_type_id === 1
                                        ? <> e vai entrar pelo <strong>trigger</strong> &quot;{selectedNode.nome || `#${selectedNode.id}`}&quot;.</>
                                        : <> e vai pular direto pro node <strong>[{nodeTypeLabel(selectedNode.node_type_id)}] {selectedNode.nome || `#${selectedNode.id}`}</strong>.</>
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={isSaving || !funnelId || !targetNodeId}
                >
                    {isSaving ? 'Salvando...' : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Configurações
                        </>
                    )}
                </Button>
            </div>
        </>
    );
}
